import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, Tag, ToggleLeft, ToggleRight } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';

interface Coupon {
  id: number;
  code: string;
  discountAmount: number;
  isActive: boolean;
  usageLimit: number | null;
  usedCount: number;
  expiryDate: string | null;
  minOrderAmount: number;
  description: string | null;
  createdAt: string;
}

const empty = (): Partial<Coupon> => ({
  code: '',
  discountAmount: 0,
  isActive: true,
  usageLimit: null,
  expiryDate: null,
  minOrderAmount: 0,
  description: '',
});

const CouponsPage: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Partial<Coupon>>(empty());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiService.getCoupons();
      setCoupons(res?.entity ?? []);
    } catch {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(empty()); setShowForm(true); };
  const openEdit = (c: Coupon) => { setEditing({ ...c }); setShowForm(true); };

  const handleSave = async () => {
    if (!editing.code?.trim()) { toast.error('Code is required'); return; }
    if (!editing.discountAmount || editing.discountAmount <= 0) { toast.error('Discount amount must be > 0'); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await apiService.updateCoupon(editing.id, editing);
        toast.success('Coupon updated');
      } else {
        await apiService.createCoupon(editing);
        toast.success('Coupon created');
      }
      setShowForm(false);
      load();
    } catch {
      toast.error('Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, code: string) => {
    if (!confirm(`Delete coupon "${code}"?`)) return;
    try {
      await apiService.deleteCoupon(id);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleToggle = async (c: Coupon) => {
    try {
      await apiService.updateCoupon(c.id, { ...c, isActive: !c.isActive });
      toast.success(c.isActive ? 'Deactivated' : 'Activated');
      load();
    } catch {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Tag className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Coupon Codes</h1>
            <p className="text-sm text-gray-500">Create and manage discount coupons</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Coupon
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
          </div>
        ) : coupons.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="mx-auto h-10 w-10 text-gray-400 mb-3" />
            <p className="text-gray-500">No coupons yet. Create your first one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Code', 'Discount', 'Min Order', 'Usage', 'Expiry', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {coupons.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="font-mono font-semibold text-gray-900">{c.code}</div>
                      {c.description && <div className="text-xs text-gray-500 mt-0.5">{c.description}</div>}
                    </td>
                    <td className="px-5 py-4 text-green-700 font-semibold">₹{c.discountAmount}</td>
                    <td className="px-5 py-4 text-gray-700">₹{c.minOrderAmount}</td>
                    <td className="px-5 py-4 text-gray-700">
                      {c.usedCount}{c.usageLimit != null ? ` / ${c.usageLimit}` : ' / ∞'}
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      {c.expiryDate ? new Date(c.expiryDate).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-5 py-4">
                      <button onClick={() => handleToggle(c)} title={c.isActive ? 'Deactivate' : 'Activate'}>
                        {c.isActive
                          ? <ToggleRight className="h-6 w-6 text-green-500" />
                          : <ToggleLeft className="h-6 w-6 text-gray-400" />}
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEdit(c)} className="text-indigo-500 hover:text-indigo-700">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDelete(c.id, c.code)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-semibold">{editing.id ? 'Edit Coupon' : 'New Coupon'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                <input
                  type="text"
                  value={editing.code ?? ''}
                  onChange={e => setEditing(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g. SANDESH100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount Amount (₹) *</label>
                <input
                  type="number"
                  min={1}
                  value={editing.discountAmount ?? ''}
                  onChange={e => setEditing(p => ({ ...p, discountAmount: Number(e.target.value) }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Order Amount (₹)</label>
                <input
                  type="number"
                  min={0}
                  value={editing.minOrderAmount ?? 0}
                  onChange={e => setEditing(p => ({ ...p, minOrderAmount: Number(e.target.value) }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usage Limit (blank = unlimited)</label>
                <input
                  type="number"
                  min={1}
                  value={editing.usageLimit ?? ''}
                  onChange={e => setEditing(p => ({ ...p, usageLimit: e.target.value ? Number(e.target.value) : null }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Unlimited"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (optional)</label>
                <input
                  type="date"
                  value={editing.expiryDate ? editing.expiryDate.substring(0, 10) : ''}
                  onChange={e => setEditing(p => ({ ...p, expiryDate: e.target.value || null }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={editing.description ?? ''}
                  onChange={e => setEditing(p => ({ ...p, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g. Special offer for Sandesh"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={editing.isActive ?? true}
                  onChange={e => setEditing(p => ({ ...p, isActive: e.target.checked }))}
                  className="h-4 w-4 text-purple-600 rounded"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">Active</label>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t">
              <button
                onClick={() => setShowForm(false)}
                className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300"
              >
                {saving ? 'Saving…' : editing.id ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponsPage;
